import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Autocomplete,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import {
  getTournamentTeams,
  createTeam,
  deleteTeam,
  enrollPlayerInTeam,
  quickRegisterAndEnrollInTeam,
  unenrollPlayerFromTeam,
  searchPlayers,
  reorderTeamPlayers,
} from '../../../services/tournament.service';
import QuickRegisterDialog from './QuickRegisterDialog';

export default function StepTeamPlayers({ torId, onNext, onBack, setAlertInfo }) {
  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [newTeamName, setNewTeamName] = useState('');
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const [localPlayers, setLocalPlayers] = useState([]);
  const [isOrderChanged, setIsOrderChanged] = useState(false);

  const fetchTeams = async () => {
    if (!torId) return;
    setTeamsLoading(true);
    try {
      const data = await getTournamentTeams(torId);
      const sortedTeams = (data || []).map((t) => ({
        ...t,
        jugadores: [...t.jugadores].sort((a, b) => (a.ordenTablero ?? 999) - (b.ordenTablero ?? 999)),
      }));
      setTeams(sortedTeams);
    } catch (error) {
      console.error('Error fetching tournament teams:', error);
      setAlertInfo({
        open: true,
        message: 'Error al obtener los equipos.',
        severity: 'error',
      });
    } finally {
      setTeamsLoading(false);
    }
  };

  useEffect(() => {
    fetchTeams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [torId]);

  useEffect(() => {
    if (selectedTeamId) {
      const activeTeam = teams.find((t) => t.id === selectedTeamId);
      if (activeTeam) {
        setLocalPlayers(activeTeam.jugadores || []);
      } else {
        setLocalPlayers([]);
      }
    } else {
      setLocalPlayers([]);
    }
    setIsOrderChanged(false);
  }, [selectedTeamId, teams]);

  const handleMovePlayerUp = (idx) => {
    if (idx === 0) return;
    const newPlayers = [...localPlayers];
    const temp = newPlayers[idx];
    newPlayers[idx] = newPlayers[idx - 1];
    newPlayers[idx - 1] = temp;
    setLocalPlayers(newPlayers);
    setIsOrderChanged(true);
  };

  const handleMovePlayerDown = (idx) => {
    if (idx === localPlayers.length - 1) return;
    const newPlayers = [...localPlayers];
    const temp = newPlayers[idx];
    newPlayers[idx] = newPlayers[idx + 1];
    newPlayers[idx + 1] = temp;
    setLocalPlayers(newPlayers);
    setIsOrderChanged(true);
  };

  const handleSaveOrder = async () => {
    if (!torId || !selectedTeamId) return;
    setLoading(true);
    try {
      const playerIds = localPlayers.map((p) => p.jug_id);
      await reorderTeamPlayers(torId, selectedTeamId, playerIds);
      setAlertInfo({
        open: true,
        message: 'Orden de tableros actualizado exitosamente.',
        severity: 'success',
      });
      await fetchTeams();
    } catch (error) {
      console.error('Error reordering team players:', error);
      setAlertInfo({
        open: true,
        message: error.message || 'Error al reordenar los tableros del equipo.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (searchInputValue.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    const delayDebounceFn = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const results = await searchPlayers(searchInputValue, torId);
        setSearchResults(results || []);
      } catch (error) {
        console.error('Error searching players:', error);
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchInputValue, torId]);

  const handleCreateTeam = async () => {
    if (!newTeamName.trim()) return;
    setLoading(true);
    try {
      const result = await createTeam(torId, newTeamName.trim());
      if (result && result.equ_id) {
        setAlertInfo({
          open: true,
          message: `Equipo "${newTeamName.trim()}" creado exitosamente.`,
          severity: 'success',
        });
        setNewTeamName('');
        await fetchTeams();
        setSelectedTeamId(result.equ_id);
      }
    } catch (error) {
      console.error('Error creating team:', error);
      setAlertInfo({
        open: true,
        message: error.message || 'Error al crear el equipo.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTeam = async (equId, name) => {
    if (!window.confirm(`¿Estás seguro de eliminar el equipo "${name}"? Se eliminarán todos sus integrantes del torneo.`)) return;
    setLoading(true);
    try {
      await deleteTeam(torId, equId);
      setAlertInfo({
        open: true,
        message: `Equipo "${name}" eliminado.`,
        severity: 'info',
      });
      if (selectedTeamId === equId) {
        setSelectedTeamId(null);
      }
      await fetchTeams();
    } catch (error) {
      console.error('Error deleting team:', error);
      setAlertInfo({
        open: true,
        message: 'Error al eliminar el equipo.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEnrollPlayerInTeam = async (event, player) => {
    if (!player || !selectedTeamId) return;
    try {
      setLoading(true);
      await enrollPlayerInTeam(torId, selectedTeamId, player.jug_id);
      setAlertInfo({
        open: true,
        message: `Jugador ${player.apellido_nombre} inscrito en el equipo.`,
        severity: 'success',
      });
      setSearchInputValue('');
      setSearchResults([]);
      await fetchTeams();
    } catch (error) {
      console.error('Error enrolling player in team:', error);
      setAlertInfo({
        open: true,
        message: error.message || 'Error al inscribir al jugador en el equipo.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUnenrollPlayerFromTeam = async (jugId, name) => {
    if (!torId || !selectedTeamId || !jugId) return;
    try {
      setLoading(true);
      await unenrollPlayerFromTeam(torId, selectedTeamId, jugId);
      setAlertInfo({
        open: true,
        message: `Jugador ${name} desvinculado del equipo.`,
        severity: 'info',
      });
      await fetchTeams();
    } catch (error) {
      console.error('Error unenrolling player from team:', error);
      setAlertInfo({
        open: true,
        message: 'Error al desvincular al jugador del equipo.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleQuickRegisterSubmit = async (newPlayerData, resetForm) => {
    setLoading(true);
    try {
      const result = await quickRegisterAndEnrollInTeam(torId, selectedTeamId, newPlayerData);
      if (result && result.jug_id) {
        setAlertInfo({
          open: true,
          message: `Jugador ${result.apellido_nombre} registrado e inscrito en el equipo.`,
          severity: 'success',
        });
        setIsDialogOpen(false);
        resetForm();
        await fetchTeams();
      } else {
        throw new Error('No se pudo registrar el jugador');
      }
    } catch (error) {
      console.error('Error registering new player:', error);
      setAlertInfo({
        open: true,
        message: error.message || 'Error al registrar e inscribir al nuevo jugador.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ color: '#cbd5e1', mb: 2, fontWeight: 600 }}>
        Paso 2: Equipos / Jugadores por equipo
      </Typography>

      <Grid container spacing={3}>
        {/* Left Column: Teams List */}
        <Grid item xs={12} md={5}>
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              background: 'rgba(15, 23, 42, 0.5)',
              borderColor: 'rgba(255, 255, 255, 0.08)',
              borderRadius: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
              minHeight: '400px',
            }}
          >
            <Box>
              <Typography variant="subtitle1" sx={{ color: '#cbd5e1', fontWeight: 600, mb: 1 }}>
                Crear Equipo
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Nombre del equipo..."
                  value={newTeamName}
                  onChange={(e) => setNewTeamName(e.target.value)}
                  InputLabelProps={{ style: { color: '#94a3b8' } }}
                  inputProps={{ style: { color: '#cbd5e1' } }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 0,
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.15)', borderRadius: 0 },
                      '&:hover fieldset': { borderColor: '#3a6eb6' },
                      '&.Mui-focused fieldset': { borderColor: '#3a6eb6' },
                    },
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleCreateTeam}
                  disabled={loading || !newTeamName.trim()}
                  sx={{
                    background: '#3a6eb6',
                    borderRadius: 0,
                    px: 3,
                    '&:hover': { background: '#2a5793' },
                    '&.Mui-disabled': { background: 'rgba(58, 110, 182, 0.3)', color: 'rgba(255, 255, 255, 0.3)' },
                  }}
                >
                  Crear
                </Button>
              </Box>
            </Box>

            <Box sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.08)', pt: 2, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
              <Typography variant="subtitle2" sx={{ color: '#cbd5e1', fontWeight: 600, mb: 2 }}>
                Equipos del Torneo ({teams.length})
              </Typography>

              {teamsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={30} />
                </Box>
              ) : teams.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1, py: 4, flexDirection: 'column', textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                    Aún no se han creado equipos.
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: '320px', overflowY: 'auto', pr: 0.5 }}>
                  {teams.map((t) => {
                    const isSelected = selectedTeamId === t.id;
                    return (
                      <Box
                        key={t.id}
                        onClick={() => setSelectedTeamId(t.id)}
                        sx={{
                          py: 1,
                          px: 1.5,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          background: isSelected ? 'rgba(58, 110, 182, 0.15)' : 'rgba(255, 255, 255, 0.02)',
                          border: isSelected ? '1px solid #3a6eb6' : '1px solid rgba(255, 255, 255, 0.05)',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          '&:hover': {
                            background: isSelected ? 'rgba(58, 110, 182, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                            borderColor: isSelected ? '#3a6eb6' : 'rgba(255, 255, 255, 0.15)',
                          },
                        }}
                      >
                        <Box>
                          <Typography variant="subtitle2" sx={{ color: '#cbd5e1', fontWeight: 600 }}>
                            {t.nombre}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                            {t.jugadores.length} {t.jugadores.length === 1 ? 'integrante' : 'integrantes'}
                          </Typography>
                        </Box>

                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteTeam(t.id, t.nombre);
                          }}
                          sx={{ color: '#ef4444', '&:hover': { background: 'rgba(239, 68, 68, 0.1)' } }}
                          disabled={loading}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Right Column: Active Team Roster */}
        <Grid item xs={12} md={7}>
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              background: 'rgba(15, 23, 42, 0.5)',
              borderColor: 'rgba(255, 255, 255, 0.08)',
              borderRadius: 0,
              minHeight: '400px',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {!selectedTeamId ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1, py: 4, flexDirection: 'column', textAlign: 'center' }}>
                <Typography variant="body1" sx={{ color: '#64748b', mb: 1, fontWeight: 500 }}>
                  Selecciona un equipo de la izquierda
                </Typography>
                <Typography variant="body2" sx={{ color: '#475569' }}>
                  Debes seleccionar un equipo para poder gestionar sus jugadores.
                </Typography>
              </Box>
            ) : (() => {
              const activeTeam = teams.find((t) => t.id === selectedTeamId);
              if (!activeTeam) return null;
              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', flexGrow: 1 }}>
                  {/* Header */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ color: '#cbd5e1', fontWeight: 600 }}>
                      Integrantes de: <span style={{ color: '#3a6eb6' }}>{activeTeam.nombre}</span>
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#3a6eb6', fontWeight: 'bold' }}>
                      Total: {localPlayers.length} jugadores
                    </Typography>
                  </Box>

                  {/* Search and Quick Register Inputs */}
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={8}>
                      <Autocomplete
                        value={null}
                        onChange={handleEnrollPlayerInTeam}
                        inputValue={searchInputValue}
                        onInputChange={(event, newInputValue) => {
                          setSearchInputValue(newInputValue);
                        }}
                        options={searchResults}
                        getOptionLabel={(option) => `${option.apellido_nombre} (DNI: ${option.documento})`}
                        loading={searchLoading}
                        noOptionsText={searchInputValue.length < 2 ? 'Escribe al menos 2 letras...' : 'No se encontraron jugadores'}
                        PaperComponent={({ children }) => (
                          <Paper sx={{ borderRadius: 0, background: '#1e293b', color: '#cbd5e1', border: '1px solid rgba(255, 255, 255, 0.15)' }}>
                            {children}
                          </Paper>
                        )}
                        ListboxProps={{
                          sx: {
                            backgroundColor: '#1e293b',
                            color: '#94a3b8',
                            '& .MuiAutocomplete-option': {
                              borderRadius: 0,
                              '&[aria-selected="true"]': {
                                backgroundColor: 'rgba(58, 110, 182, 0.25) !important',
                                color: '#cbd5e1',
                              },
                              '&[aria-selected="true"].Mui-focused': {
                                backgroundColor: 'rgba(58, 110, 182, 0.3) !important',
                              },
                              '&.Mui-focused': {
                                backgroundColor: 'rgba(255, 255, 255, 0.08) !important',
                              },
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                              },
                            },
                          },
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Inscribir Jugador Existente"
                            placeholder="Ej: Pérez, Juan"
                            InputLabelProps={{ style: { color: '#94a3b8' } }}
                            inputProps={{ ...params.inputProps, style: { color: '#cbd5e1' } }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.15)' },
                                '&:hover fieldset': { borderColor: '#3a6eb6' },
                                '&.Mui-focused fieldset': { borderColor: '#3a6eb6' },
                              },
                            }}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <React.Fragment>
                                  {searchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                  {params.InputProps.endAdornment}
                                </React.Fragment>
                              ),
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<PersonAddIcon />}
                        onClick={() => setIsDialogOpen(true)}
                        sx={{
                          height: '56px',
                          color: '#3a6eb6',
                          borderColor: 'rgba(58, 110, 182, 0.3)',
                          borderRadius: 0,
                          '&:hover': {
                            borderColor: '#3a6eb6',
                            background: 'rgba(58, 110, 182, 0.05)',
                          },
                        }}
                      >
                        Nuevo Jugador
                      </Button>
                    </Grid>
                  </Grid>

                  {/* Roster Table */}
                  {localPlayers.length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1, py: 4, flexDirection: 'column', textAlign: 'center', border: '1px dashed rgba(255, 255, 255, 0.08)' }}>
                      <Typography variant="body2" sx={{ color: '#64748b' }}>
                        Este equipo no tiene jugadores inscritos aún.
                      </Typography>
                    </Box>
                  ) : (
                    <TableContainer sx={{ maxHeight: '300px', border: '1px solid rgba(255, 255, 255, 0.05)', flexGrow: 1 }}>
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ py: 0.5, background: '#0f172a', color: '#94a3b8', fontWeight: 'bold', borderColor: 'rgba(255, 255, 255, 0.08)' }}>Tablero</TableCell>
                            <TableCell sx={{ py: 0.5, background: '#0f172a', color: '#94a3b8', fontWeight: 'bold', borderColor: 'rgba(255, 255, 255, 0.08)' }}>Jugador</TableCell>
                            <TableCell sx={{ py: 0.5, background: '#0f172a', color: '#94a3b8', fontWeight: 'bold', borderColor: 'rgba(255, 255, 255, 0.08)' }}>DNI</TableCell>
                            <TableCell sx={{ py: 0.5, background: '#0f172a', color: '#94a3b8', fontWeight: 'bold', borderColor: 'rgba(255, 255, 255, 0.08)' }}>ELO</TableCell>
                            <TableCell align="right" sx={{ py: 0.5, background: '#0f172a', color: '#94a3b8', fontWeight: 'bold', borderColor: 'rgba(255, 255, 255, 0.08)' }}>Acciones</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {localPlayers.map((player, idx) => (
                            <TableRow key={player.jug_id || idx} hover sx={{ '&:hover': { background: 'rgba(255, 255, 255, 0.02) !important' } }}>
                              <TableCell sx={{ py: 0.5, color: '#94a3b8', borderColor: 'rgba(255, 255, 255, 0.05)' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Typography variant="body2" sx={{ color: '#cbd5e1', fontWeight: 600, minWidth: '35px' }}>
                                    # {idx + 1}
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleMovePlayerUp(idx)}
                                    disabled={idx === 0 || loading}
                                    sx={{ p: 0.1, color: '#3a6eb6', '&.Mui-disabled': { color: 'rgba(255,255,255,0.05)' } }}
                                  >
                                    <ArrowUpwardIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleMovePlayerDown(idx)}
                                    disabled={idx === localPlayers.length - 1 || loading}
                                    sx={{ p: 0.1, color: '#3a6eb6', '&.Mui-disabled': { color: 'rgba(255,255,255,0.05)' } }}
                                  >
                                    <ArrowDownwardIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                                  </IconButton>
                                </Box>
                              </TableCell>
                              <TableCell sx={{ py: 0.5, color: '#cbd5e1', fontWeight: 500, borderColor: 'rgba(255, 255, 255, 0.05)' }}>{player.apellido_nombre}</TableCell>
                              <TableCell sx={{ py: 0.5, color: '#94a3b8', borderColor: 'rgba(255, 255, 255, 0.05)' }}>{player.documento || '—'}</TableCell>
                              <TableCell sx={{ py: 0.5, color: '#94a3b8', borderColor: 'rgba(255, 255, 255, 0.05)' }}>{player.elo ?? 0}</TableCell>
                              <TableCell align="right" sx={{ py: 0.5, borderColor: 'rgba(255, 255, 255, 0.05)' }}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleUnenrollPlayerFromTeam(player.jug_id, player.apellido_nombre)}
                                  sx={{ p: 0.25, color: '#ef4444', '&:hover': { background: 'rgba(239, 68, 68, 0.1)' } }}
                                  disabled={loading}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}

                  {isOrderChanged && (
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-start' }}>
                      <Button
                        variant="contained"
                        onClick={handleSaveOrder}
                        disabled={loading}
                        sx={{
                          borderRadius: 0,
                          background: '#10b981',
                          color: '#fff',
                          fontWeight: 600,
                          px: 3,
                          '&:hover': { background: '#059669' },
                          '&.Mui-disabled': { background: 'rgba(16, 185, 129, 0.3)', color: 'rgba(255,255,255,0.3)' },
                        }}
                      >
                        Confirmar reordenamiento
                      </Button>
                    </Box>
                  )}
                </Box>
              );
            })()}
          </Paper>
        </Grid>
      </Grid>

      {/* Bottom Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, alignItems: 'center' }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          disabled={isOrderChanged || loading}
          sx={{
            color: '#94a3b8',
            borderColor: 'rgba(255, 255, 255, 0.2)',
            borderRadius: 0,
            '&:hover': { borderColor: '#cbd5e1', background: 'rgba(255, 255, 255, 0.05)' },
            '&.Mui-disabled': { borderColor: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.1)' },
          }}
        >
          Atrás
        </Button>
        {isOrderChanged && (
          <Typography variant="body2" sx={{ color: '#ef4444', fontWeight: 'bold' }}>
            Tienes cambios en el orden de tableros sin confirmar
          </Typography>
        )}
        <Button
          variant="contained"
          endIcon={<ArrowForwardIcon />}
          onClick={onNext}
          disabled={isOrderChanged || loading}
          sx={{
            background: '#3a6eb6',
            borderRadius: 0,
            '&:hover': { background: '#2a5793' },
            '&.Mui-disabled': { background: 'rgba(58, 110, 182, 0.1)', color: 'rgba(255,255,255,0.2)' },
          }}
        >
          Siguiente
        </Button>
      </Box>

      {/* Dialog for Quick Player Registration in Team */}
      <QuickRegisterDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onSubmit={handleQuickRegisterSubmit}
        loading={loading}
      />
    </Box>
  );
}
